import React, { useState, useEffect } from 'react';
import { client, urlFor } from '../../client'; // Adjust client path if needed
import { PortableText } from '@portabletext/react';

// Custom component to render the table
const TableComponent = ({ value, components }) => {
  const { rows = [] } = value;
  if (!rows) return null;

  return (
    <div className="overflow-x-auto my-4 border border-gray-200 rounded-lg shadow-sm">
      <table className="min-w-full text-sm text-gray-700">
        <tbody>
          {rows.map((row) => (
            <tr key={row._key} className="border-b border-gray-200 last:border-b-0 hover:bg-gray-50">
              {row.cells.map((cell, index) => (
                <td key={cell._key} className={`py-2 px-4 ${index === 0 ? 'font-medium text-gray-900' : ''}`}>
                  {/* Render cell content, passing down the components */}
                  <PortableText value={cell.content || []} components={components} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};


// Define portableTextComponents including the table
const portableTextComponents = {
  types: {
    table: TableComponent, // Use the custom component for tables
    // image: ({ value }) => { ... } // Optional: Add if you need custom image handling
  },
  list: {
    bullet: ({children}) => <ul className="list-disc pl-5 mb-4 space-y-1 text-gray-700">{children}</ul>,
    number: ({children}) => <ol className="list-decimal pl-5 mb-4 space-y-1 text-gray-700">{children}</ol>,
  },
  listItem: {
    bullet: ({children}) => <li className="flex items-start"><span className="w-2 h-2 bg-[#691e31] rounded-full mr-2 mt-[0.6em] flex-shrink-0"></span><span className="flex-1">{children}</span></li>,
    number: ({children}) => <li className="ml-2">{children}</li>,
  },
  block: {
    normal: ({children}) => <p className="mb-2 text-gray-700">{children}</p>,
    h4: ({children}) => <h4 className="font-medium mb-2 text-[#691e31]">{children}</h4>,
  },
  marks: {
    strong: ({children}) => <strong className="font-medium text-gray-900">{children}</strong>,
    em: ({children}) => <em className="text-sm italic text-gray-600">{children}</em>,
  }
};

// Placeholder for loading state
const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
  </div>
);

// Placeholder for error state
const ErrorMessage = ({ message }) => (
  <div className="container mx-auto px-4 py-12 text-center">
    <p className="text-red-600">{message || 'Error loading page content.'}</p>
  </div>
);

const SecondGrade = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(null);

  useEffect(() => {
    const query = `*[_type == "secondGradePage" && _id == "secondGradePageContent"][0]{
      ...,
      bannerImage { asset->{ _id, url } },
      tabs[]{
        _key,
        tabTitle,
        tabIdentifier { current },
        contentSections[]{
          _key,
          title,
          body // Fetch the full body array, including table data
        }
      },
      newsletterTitle,
      newsletterSubtitle,
      teacherName,
      newsletterContent
    }`;

    setLoading(true);
    client.fetch(query)
      .then((data) => {
        if (data) {
          setPageData(data);
          if (data.tabs && data.tabs.length > 0) {
            setActiveTab(data.tabs[0].tabIdentifier.current);
          }
        } else {
          setError('Second Grade Page content not found.');
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching Second Grade page data:', err);
        setError('Failed to fetch page content.');
        setLoading(false);
      });
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !pageData) {
    return <ErrorMessage message={error || 'Page data is unavailable.'} />;
  }

  // Destructure page data
  const {
    bannerImage,
    bannerTitle = 'Second Grade',
    tabs = [],
    newsletterTitle = 'Monthly Newsletter',
    newsletterSubtitle = 'Newsletter',
    teacherName = 'Teacher',
    newsletterContent = []
  } = pageData;

  // Find the content for the active tab
  const activeTabData = tabs.find(tab => tab.tabIdentifier.current === activeTab);

  return (
    <div className="bg-white">
      {/* Hero Banner */}
      <section className="relative h-[40vh] max-h-[400px]">
        <div className="w-full h-full relative overflow-hidden">
          {bannerImage?.asset?.url ? (
          <img 
              src={urlFor(bannerImage).width(1200).url()}
              alt={bannerTitle || 'Second Grade Banner'}
            className="absolute inset-0 w-full h-full object-cover object-center" 
          />
          ) : (
            <div className="absolute inset-0 w-full h-full bg-gray-300"></div> // Placeholder
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20"></div>
        </div>
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-6 md:px-12 lg:px-16">
            <div className="max-w-3xl pb-12 md:pb-16 lg:pb-16 text-left">
              <div className="w-16 h-1 bg-yellow-500 mb-6"></div>
              <h1 className="text-white text-4xl md:text-5xl font-bold mb-2 leading-tight text-left">
                {bannerTitle}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-6xl mx-auto">
            {/* Tabs - Use rounded button style */}
            {tabs.length > 0 && (
              <div className="flex overflow-x-auto pb-2 mb-8">
                {tabs.map((tab) => (
              <button
                    key={tab._key}
                    className={`py-3 px-6 font-bold rounded-full mr-4 transition-colors whitespace-nowrap ${
                      activeTab === tab.tabIdentifier.current
                        ? 'bg-[#691e31] text-white shadow-md'
                        : 'bg-[#2a4865]/10 text-[#2a4865] hover:bg-[#2a4865]/20'
                    }`}
                    onClick={() => setActiveTab(tab.tabIdentifier.current)}
                  >
                    {tab.tabTitle}
              </button>
                ))}
                
                {/* Newsletter Tab */}
                <button
                  className={`py-3 px-6 font-bold rounded-full mr-4 transition-colors whitespace-nowrap ${
                    activeTab === 'newsletter'
                      ? 'bg-[#691e31] text-white shadow-md'
                      : 'bg-[#2a4865]/10 text-[#2a4865] hover:bg-[#2a4865]/20'
                  }`}
                  onClick={() => setActiveTab('newsletter')}
                >
                  {newsletterTitle}
                </button>
              </div>
            )}
            
            {/* Tab Content */}
            {activeTabData && activeTab !== 'newsletter' && (
              <div className="space-y-6">
                {activeTabData.contentSections?.map((section) => (
                  <section key={section._key} className="bg-[#2a4865]/5 p-5 rounded-lg border-l-4 border-[#2a4865]">
                    <h3 className="text-xl font-bold text-[#2a4865] mb-3 relative after:content-[''] after:absolute after:w-10 after:h-0.5 after:bg-yellow-500 after:-bottom-1 after:left-0">
                      {section.title}
                    </h3>
                    {section.body && (
                      <PortableText
                        value={section.body}
                        components={portableTextComponents} // Pass components including table renderer
                      />
                    )}
                </section>
                ))}
              </div>
            )}

            {/* Newsletter Tab Content */}
            {activeTab === 'newsletter' && (
              <div className="space-y-6">
                <section className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  {/* Newsletter Header */}
                  <div className="bg-[#2a4865] text-white p-6 rounded-t-lg">
                    <h3 className="text-2xl font-bold mb-2 text-white">{newsletterSubtitle}</h3>
                    <p className="text-white/80 text-sm">Second Grade Class • {teacherName}</p>
                  </div>
                  
                  {/* Newsletter Content from Sanity */}
                  <div className="p-6 prose prose-lg max-w-none">
                    {newsletterContent && newsletterContent.length > 0 ? (
                      <PortableText
                        value={newsletterContent}
                        components={{
                          ...portableTextComponents,
                          block: {
                            ...portableTextComponents.block,
                            h2: ({children}) => <h2 className="text-xl font-bold text-[#691e31] mb-4">{children}</h2>,
                            h3: ({children}) => <h3 className="text-lg font-bold text-[#691e31] mb-3">{children}</h3>,
                            normal: ({children}) => <p className="text-gray-700 mb-4">{children}</p>
                          },
                          list: {
                            bullet: ({children}) => <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">{children}</ul>,
                            number: ({children}) => <ol className="list-decimal pl-6 space-y-2 text-gray-700 mb-6">{children}</ol>
                          },
                          listItem: {
                            bullet: ({children}) => <li>{children}</li>,
                            number: ({children}) => <li>{children}</li>
                          }
                        }}
                      />
                    ) : (
                      <p className="text-gray-500 italic">Newsletter content coming soon...</p>
                    )}
                    
                    {/* Footer Contact Section */}
                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded mt-6">
                      <h3 className="font-bold text-[#691e31] mb-2">📧 Learning Partners!</h3>
                      <p className="text-gray-700 text-sm">
                        Your support at home makes such a difference in your child's learning. 
                        Please don't hesitate to reach out if you have questions or want to share successes!
                      </p>
                      <p className="text-gray-700 text-sm mt-2">
                        <strong>Together we can achieve great things!</strong><br/>
                        <em>- {teacherName}, Second Grade Teacher</em>
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default SecondGrade; 